import { appPermissions } from "../../_constants/permissions.constants";

export type SpotBannerStatus = "FREE" | "PENDING" | "OCCUPIED";

export interface SpotBannerInputs {
    holderEmail: string | null | undefined;
    holderUserUid: string | null | undefined;
    assignmentsCount: number;
    maxAssignments: number;
}

// Resolves the banner status used in the parking list card. Rules:
//   - "PENDING"  → spot has a holder whose invitation has not been accepted
//                  (holderEmail present but no user_uid).
//   - "FREE"     → there is still at least one delegation slot left AND the
//                  holder (if any) has already accepted. `maxAssignments === 0`
//                  implies the spot is taken by the holder alone, so never FREE.
//   - "OCCUPIED" → otherwise (holder accepted + no free slots, or
//                  max_assignments = 0).
// Note: "FREE" reflects slot availability for assignees, not the mere absence
// of a holder — a spot with an accepted holder and delegation room left is
// still considered FREE (more people can be invited to use it).
export function resolveSpotBannerStatus({
    holderEmail,
    holderUserUid,
    assignmentsCount,
    maxAssignments,
}: SpotBannerInputs): SpotBannerStatus {
    const isHolderPending = !!holderEmail && !holderUserUid;
    if (isHolderPending) return "PENDING";

    const isFree = maxAssignments > 0 && assignmentsCount === 0;
    return isFree ? "FREE" : "OCCUPIED";
}

export interface ParkingPermissionInputs {
    locationPermission: number | null | undefined;
    currentUserEmail: string | null | undefined;
    spotHolderEmail: string | null | undefined;
}

export interface ParkingPermissionResult {
    isAdmin: boolean;
    canManage: boolean;
    canManageAssignments: boolean;
}

export interface AssignmentsVisibilityInputs {
    canManageAssignments: boolean;
    spotHolderEmail: string | null | undefined;
    spotHolderUserUid: string | null | undefined;
    assignmentsCount: number;
    maxAssignments: number;
}

export interface AssignmentsVisibilityResult {
    // Whole assignments block (list + banner + form) is rendered
    showSection: boolean;
    // "Holder has not accepted yet" notice — replaces the add form
    showHolderNotAcceptedBanner: boolean;
    // Add-new-assignment form is rendered
    showAddForm: boolean;
}

// Encapsulates the render gates for the assignments section in
// `ParkingSpotConfigView`. The rules:
//   - Never show anything if the viewer can't manage assignments.
//   - Never show anything if the spot has no holder at all (no email).
//   - When the holder has not accepted yet (user_uid missing), show the
//     "not accepted" banner instead of the add form.
//   - Hide the add form once all assignment slots are taken.
export function resolveAssignmentsVisibility({
    canManageAssignments,
    spotHolderEmail,
    spotHolderUserUid,
    assignmentsCount,
    maxAssignments,
}: AssignmentsVisibilityInputs): AssignmentsVisibilityResult {
    if (!canManageAssignments || !spotHolderEmail) {
        return {
            showSection: false,
            showHolderNotAcceptedBanner: false,
            showAddForm: false,
        };
    }

    const holderAccepted = !!spotHolderUserUid;
    const hasFreeSlot = assignmentsCount < maxAssignments;

    return {
        showSection: true,
        showHolderNotAcceptedBanner: !holderAccepted,
        showAddForm: holderAccepted && hasFreeSlot,
    };
}

// Pure, side-effect free resolver so the permission matrix can be unit-tested
// directly. Kept in sync with the `ParkingSpotConfigView` consumer.
export function resolveParkingPermissions({
    locationPermission,
    currentUserEmail,
    spotHolderEmail,
}: ParkingPermissionInputs): ParkingPermissionResult {
    const isAdmin = appPermissions.canUserManageParking(locationPermission);
    const canManage = isAdmin;

    const isActingAsHolder =
        !isAdmin &&
        !!currentUserEmail &&
        spotHolderEmail === currentUserEmail;

    return {
        isAdmin,
        canManage,
        canManageAssignments: canManage || isActingAsHolder,
    };
}
